import { useEffect, useRef } from 'react';
import { ZOOM_MAX, ZOOM_MIN } from '../../constants';

export const useTouchZoom = ({
  scrollContainerRef,
  zoomRef,
  setZoom,
  setNodes,
  supportsPointer = false
}) => {
  const pinchDistRef = useRef(0);
  const initialZoomPinchRef = useRef(1);
  const nodeMoveStartRef = useRef(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const getDist = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        nodeMoveStartRef.current = null;
        pinchDistRef.current = getDist(e.touches);
        initialZoomPinchRef.current = zoomRef.current;
      } else if (e.touches.length === 1) {
        if (supportsPointer) return;
        // Find if we are touching a node
        let target = e.target;
        let targetNode = null;
        
        // Robust way to find the node group
        while (target && target !== container) {
          if (target.classList && target.classList.contains('node-item-g')) {
            targetNode = target;
            break;
          }
          target = target.parentNode;
        }
        
        if (targetNode) {
          const nodeId = targetNode.getAttribute('data-node-id');
          if (nodeId) {
            // Start dragging this node immediately
            nodeMoveStartRef.current = { 
              x: e.touches[0].clientX, 
              y: e.touches[0].clientY,
              id: nodeId
            };
          }
        }
      }
    };

    const onTouchMove = (e) => {
      // Handle node moving
      if (e.touches.length === 1 && nodeMoveStartRef.current?.id) {
        if (supportsPointer) return;
        e.preventDefault();
        const dx = (e.touches[0].clientX - nodeMoveStartRef.current.x) / zoomRef.current;
        const dy = (e.touches[0].clientY - nodeMoveStartRef.current.y) / zoomRef.current;
        const id = nodeMoveStartRef.current.id;
        
        setNodes(prev => prev.map(n => n.id === id ? { ...n, x: n.x + dx, y: n.y + dy } : n));
        nodeMoveStartRef.current = { 
          ...nodeMoveStartRef.current,
          x: e.touches[0].clientX, 
          y: e.touches[0].clientY 
        };
        return;
      }

      // Handle pinch-to-zoom
      if (e.touches.length === 2 && pinchDistRef.current > 0) {
        e.preventDefault();
        const newDist = getDist(e.touches);
        const ratio = newDist / pinchDistRef.current;
        
        // Smoothing factor to avoid jumps
        const lerpRatio = 1 + (ratio - 1) * 0.8;
        const nextZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, initialZoomPinchRef.current * lerpRatio));
        
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        const rect = container.getBoundingClientRect();
        const mouseX = cx - rect.left;
        const mouseY = cy - rect.top;
        
        const currentZoom = zoomRef.current;
        const svgX = (mouseX + container.scrollLeft) / currentZoom;
        const svgY = (mouseY + container.scrollTop) / currentZoom;
        
        // Set state but also calculate scroll immediately for next frame
        setZoom(nextZoom);
        
        // Sync scroll with new zoom level to keep point under fingers
        container.scrollLeft = svgX * nextZoom - mouseX;
        container.scrollTop = svgY * nextZoom - mouseY;
      }
    };

    const onTouchEnd = () => {
      nodeMoveStartRef.current = null;
      pinchDistRef.current = 0;
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    
    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };
  }, [scrollContainerRef, zoomRef, setZoom, setNodes, supportsPointer]);

  return null;
};
